import { fmtDate, ROLES, type AdminMember, type MemberRole, type TeamAdmin, type Viewer } from "@boem/core/shared";
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api.ts";
import { adminApi, type MediaStatus, type MemberPatch, type NewMemberInput } from "../mediaApi.ts";
import "../team.css";
import { Avatar } from "../util.tsx";
import { ErrorLine, Modal, SecretHint, useSecretCheck } from "./Modal.tsx";

// The team admin screen (admins only): who is on the board, their role and
// flags, deactivate/reactivate, and the audit trail of every change.

const say = (e: unknown) => (e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");

const SOURCE: Record<string, string> = { web: "admin screen", system: "system", import: "import" };

function AddMember({ onCancel, onAdded }: { onCancel: () => void; onAdded: () => void }) {
  const [f, setF] = useState<NewMemberInput>({ name: "", email: "", role: "reporter", canBuild: false, canApprove: false, isVerifier: false, isAdmin: false });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const secrets = useSecretCheck(f.name, f.email ?? "");
  const set = <K extends keyof NewMemberInput>(k: K, v: NewMemberInput[K]) => setF((cur) => ({ ...cur, [k]: v }));
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.add({ ...f, name: f.name.trim(), email: f.email?.trim() || null });
      onAdded();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="tm-add">
      <div className="form-grid">
        <div>
          <label htmlFor="tm-name">Name (as it shows on the board)</label>
          <input id="tm-name" type="text" maxLength={40} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Priya" />
        </div>
        <div>
          <label htmlFor="tm-email">Work (Microsoft) email</label>
          <input id="tm-email" type="email" maxLength={254} value={f.email ?? ""} onChange={(e) => set("email", e.target.value)} placeholder="name@company.com" />
        </div>
      </div>
      <div className="tm-fields" style={{ marginTop: 10 }}>
        <label className="tm-field">
          Role
          <select className="search" value={f.role} onChange={(e) => set("role", e.target.value as MemberRole)}>
            {ROLES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label className="tm-check">
          <input type="checkbox" checked={f.canBuild} onChange={(e) => set("canBuild", e.target.checked)} /> Can build
        </label>
        <label className="tm-check">
          <input type="checkbox" checked={f.canApprove} onChange={(e) => set("canApprove", e.target.checked)} /> Can approve
        </label>
        <label className="tm-check" title="Checks fixes after they're deployed: every deployed item waits for them too">
          <input type="checkbox" checked={f.isVerifier} onChange={(e) => set("isVerifier", e.target.checked)} /> Verifies fixes
        </label>
        <label className="tm-check">
          <input type="checkbox" checked={f.isAdmin} onChange={(e) => set("isAdmin", e.target.checked)} /> Admin
        </label>
      </div>
      <div className="tm-role-help">{ROLES.find((r) => r.id === f.role)?.can}</div>
      <SecretHint findings={secrets} />
      <div className="row">
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn primary" disabled={busy || !f.name.trim() || secrets.length > 0} onClick={submit}>
          Add to the board
        </button>
      </div>
      <ErrorLine error={error} />
    </div>
  );
}

function MemberRow({
  m,
  me,
  busy,
  onSave,
  onActive,
}: {
  m: AdminMember;
  me: boolean;
  busy: boolean;
  onSave: (patch: MemberPatch) => void;
  onActive: (active: boolean) => void;
}) {
  const [email, setEmail] = useState(m.email ?? "");
  useEffect(() => setEmail(m.email ?? ""), [m.email]);
  const changed = email.trim().toLowerCase() !== (m.email ?? "");
  const secrets = useSecretCheck(email);
  const lockAdmin = (me && m.isAdmin) || m.adminByEnv;
  return (
    <div className={`tm-row${m.active ? "" : " tm-inactive"}`}>
      <div className="tm-head">
        <Avatar name={m.name} />
        <b>{m.name}</b>
        {me && <span className="tm-tag">you</span>}
        {m.isBot && <span className="tm-tag">bot</span>}
        {!m.active && <span className="tm-tag tm-off">deactivated</span>}
        {m.adminByEnv && (
          <span className="tm-tag" title="Admin through ADMIN_EMAILS on the server">
            admin (server)
          </span>
        )}
        <span className="tm-meta">
          {m.openItems ? `${m.openItems} open item${m.openItems === 1 ? "" : "s"} · ` : ""}since {fmtDate(m.createdAt)}
        </span>
      </div>
      <div className="tm-fields">
        <label className="tm-field tm-email">
          Email
          <span className="tm-inline">
            <input
              type="email"
              value={email}
              disabled={busy || me}
              title={me ? "Ask another admin to change your own email" : undefined}
              placeholder="no email - can't sign in yet"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && changed && !secrets.length && onSave({ email: email.trim() || null })}
            />
            {changed && (
              <button className="btn" disabled={busy || secrets.length > 0} onClick={() => onSave({ email: email.trim() || null })}>
                Save
              </button>
            )}
          </span>
        </label>
        <label className="tm-field">
          Role
          <select className="search" value={m.role} disabled={busy} onChange={(e) => onSave({ role: e.target.value as MemberRole })}>
            {ROLES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label className="tm-check">
          <input type="checkbox" checked={m.canBuild} disabled={busy} onChange={(e) => onSave({ canBuild: e.target.checked })} /> Can build
        </label>
        <label className="tm-check">
          <input type="checkbox" checked={m.canApprove} disabled={busy} onChange={(e) => onSave({ canApprove: e.target.checked })} /> Can approve
        </label>
        <label className="tm-check" title="Checks fixes after they're deployed: every deployed item waits for them too">
          <input type="checkbox" checked={m.isVerifier} disabled={busy} onChange={(e) => onSave({ isVerifier: e.target.checked })} /> Verifies fixes
        </label>
        <label className="tm-check" title={lockAdmin ? (m.adminByEnv ? "Set by ADMIN_EMAILS on the server" : "You can't remove your own admin rights") : undefined}>
          <input type="checkbox" checked={m.isAdmin} disabled={busy || lockAdmin} onChange={(e) => onSave({ isAdmin: e.target.checked })} /> Admin
        </label>
        <button
          className="btn tm-active"
          disabled={busy || me || m.adminByEnv}
          title={me ? "You can't deactivate yourself" : m.adminByEnv ? "Remove them from ADMIN_EMAILS first" : undefined}
          onClick={() => onActive(!m.active)}
        >
          {m.active ? "Deactivate" : "Reactivate"}
        </button>
      </div>
      <SecretHint findings={secrets} />
    </div>
  );
}

function MediaPanel({ status, onScan, onJump }: { status: MediaStatus; onScan: () => void; onJump: (n: number) => void }) {
  const c = status.counts;
  const backlog = (c.unscanned ?? 0) + (c.pending ?? 0);
  return (
    <div className="tm-media">
      <div>
        <b>Credential scan of screenshots:</b> {c.clean ?? 0} clear &middot; {c.quarantined ?? 0} hidden &middot; {backlog} not checked yet
        {backlog > 0 && (
          <button className="xref" type="button" onClick={onScan}>
            check them now
          </button>
        )}
      </div>
      {status.hidden.length > 0 && (
        <div className="tm-hidden">
          Hidden:{" "}
          {status.hidden.map((h, i) => (
            <span key={h.id}>
              {i ? ", " : ""}
              <button className="xref" type="button" onClick={() => onJump(h.itemNum)}>
                #{h.itemNum}
              </button>{" "}
              ({h.labels.join(", ") || "credential"})
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function TeamAdminModal({
  viewer,
  onClose,
  onChanged,
  onJump,
}: {
  viewer: Viewer;
  onClose: () => void;
  onChanged: () => void;
  onJump: (num: number) => void;
}) {
  const [data, setData] = useState<TeamAdmin | null>(null);
  const [media, setMedia] = useState<MediaStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showInactive, setShowInactive] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await adminApi.team());
    } catch (e) {
      setError(say(e));
    }
    adminApi.media().then(setMedia, () => {});
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async (id: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(id);
    setError(null);
    setNote(null);
    try {
      await fn();
      if (done) setNote(done);
      await load();
      onChanged();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(null);
    }
  };

  const setActive = (m: AdminMember, active: boolean) => {
    if (!active) {
      const open = m.openItems ? ` ${m.openItems} open item${m.openItems === 1 ? " is" : "s are"} still assigned to them.` : "";
      if (!window.confirm(`Deactivate ${m.name}? They won't be able to sign in and drop out of the pickers. Their history stays.${open}`)) return;
    }
    void save(m.id, () => adminApi.setActive(m.id, active), active ? `${m.name} can sign in again.` : `${m.name} is deactivated.`);
  };

  const members = (data?.members ?? []).filter((m) => showInactive || m.active);
  const inactive = (data?.members ?? []).filter((m) => !m.active).length;
  return (
    <Modal onClose={onClose} wide>
      <h3>Team</h3>
      <p>Who can sign in and what they can do. Sign-in is matched by work email. Every change is recorded below.</p>
      {!adding ? (
        <button className="btn" onClick={() => setAdding(true)}>
          Add someone
        </button>
      ) : (
        <AddMember
          onCancel={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            setNote("Added. They can sign in once they have an email here.");
            void load();
            onChanged();
          }}
        />
      )}
      <ErrorLine error={error} />
      {note && !error && <div className="msg ok">{note}</div>}
      {!data && !error && <p className="count-line">Loading&hellip;</p>}
      {data && (
        <>
          {inactive > 0 && (
            <label className="tm-check tm-filter">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show {inactive} deactivated
            </label>
          )}
          <div className="tm-list">
            {members.map((m) => (
              <MemberRow
                key={m.id}
                m={m}
                me={m.id === viewer.id}
                busy={busy === m.id}
                onSave={(patch) => void save(m.id, () => adminApi.update(m.id, patch))}
                onActive={(active) => setActive(m, active)}
              />
            ))}
          </div>
          {media && (
            <MediaPanel
              status={media}
              onJump={onJump}
              onScan={() => void save("media", () => adminApi.scanBacklog(), "Queued. Results show up here and on the cards as each file is checked.")}
            />
          )}
          <h4 className="tm-h4">Changes</h4>
          {data.events.length === 0 ? (
            <p className="count-line">No changes recorded yet.</p>
          ) : (
            <ol className="hist">
              {data.events.map((e, i) => (
                <li key={i}>
                  <span className="hist-when">{fmtDate(e.at)}</span>
                  <span className="hist-what">
                    <b>{e.actor ?? "System"}</b> {e.text}
                  </span>
                  <span className="hist-src">{SOURCE[e.source] ?? e.source}</span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
      <div className="row">
        <button className="btn primary" onClick={onClose}>
          Done
        </button>
      </div>
      <div className="hint">
        config/team.json only seeds the team: <code>pnpm team:sync</code> adds people who are missing and fills in empty emails, but never
        changes a role or flag set here. People in ADMIN_EMAILS on the server are always admins.
      </div>
    </Modal>
  );
}
